import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, DatePicker, Typography, InputNumber, Alert, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Lead } from '../../types/leads';
import type { AdministratorLead } from '../../types/administratorLeads';
import { getFullName } from '../../utils/text';

const { Title } = Typography;
const { Option } = Select;

interface OpportunityModalProps {
  open: boolean;
  onOk: (values: any) => void;
  onCancel: () => void;
  form: any;
  leads: Lead[] | AdministratorLead[];
  editingOpportunity: Lead | AdministratorLead | null;
  defaultLeadId?: string;
}

const OpportunityModal: React.FC<OpportunityModalProps> = ({
  open,
  onOk,
  onCancel,
  form,
  leads,
  editingOpportunity,
  defaultLeadId,
}) => {
  const { t } = useTranslation(['common', 'sales']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setError(null);
      setLoading(false);
    }
  }, [open]);

  const handleOk = async () => {
    try {
      setError(null);
      setLoading(true);
      const values = await form.validateFields();
      // onOk may be async and may throw
      await Promise.resolve(onOk(values));
    } catch (err: any) {
      if (err && err.errorFields) {
        // Validation error, handled by antd
        setError(null);
      } else {
        let errorMsg = t('sales:errors.saveFailed', { entity: t('opportunities.opportunity') });
        if (err?.message) errorMsg += ': ' + err.message;
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Title level={4}>
          {editingOpportunity
            ? t('sales:opportunities.edit')
            : t('sales:opportunities.add')}
        </Title>
      }
      open={open}
      onOk={handleOk}
      onCancel={() => {
        setError(null);
        onCancel();
      }}
      width={800}
      destroyOnHidden
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          lead_id: defaultLeadId,
          stage: 'prospecting',
        }}
      >
        <Form.Item
          name="lead_id"
          label={t('sales:opportunities.fields.lead')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <Select
            placeholder={t('sales:opportunities.selectLead')}
            disabled={!!editingOpportunity}
          >
            {leads.map(lead => (
              <Option key={lead.id} value={lead.id}>
                {getFullName(lead.name)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="stage"
          label={t('sales:opportunities.fields.stage')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <Select>
            <Option value="prospecting">{t('sales:opportunities.stages.prospecting')}</Option>
            <Option value="qualification">{t('sales:opportunities.stages.qualification')}</Option>
            <Option value="proposal">{t('sales:opportunities.stages.proposal')}</Option>
            <Option value="negotiation">{t('sales:opportunities.stages.negotiation')}</Option>
            <Option value="closed_won">{t('sales:opportunities.stages.closed_won')}</Option>
            <Option value="closed_lost">{t('sales:opportunities.stages.closed_lost')}</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="units"
          label={t('sales:opportunities.fields.units')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="follow_up_date"
          label={t('sales:opportunities.fields.expectedCloseDate')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="price"
          label={t('sales:opportunities.fields.value')}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Title level={5} style={{ marginTop: 24 }}>{t('sales:activities.title')}</Title>
        <Form.Item
          name="activity_type"
          label={t('sales:activities.fields.type')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <Select>
            <Option value="Call">{t('sales:activities.types.call')}</Option>
            <Option value="Meeting">{t('sales:activities.types.meeting')}</Option>
            <Option value="Email">{t('sales:activities.types.email')}</Option>
            <Option value="Note">{t('sales:activities.types.note')}</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="activity_summary"
          label={t('sales:activities.fields.summary')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>
        {/* Error Alert above dialog action buttons */}
        {error && (
          <Alert 
            type="error" 
            message={error} 
            showIcon 
            style={{ marginTop: 16, marginBottom: 0 }} 
          />
        )}
      </Form>
    </Modal>
  );
};

export default OpportunityModal; 